import { z } from 'zod';

/** One additive payment projection shared by session detail and attendee rows. */
export const bookingPaymentStorySchema = z.object({
  bookedAt: z.string().datetime(),
  bookedPrice: z.number().nullable(),
  paymentStatus: z.enum(['UNPAID', 'PENDING', 'PAID', 'REFUNDED', 'FAILED', 'WAIVED']),
  paymentProgressStatus: z.string().nullable(),
  canMarkPaid: z.boolean(),
  canRefund: z.boolean(),
  paidAt: z.string().datetime().nullable(),
  paymentAmount: z.number().nullable(),
  paymentMethod: z.string().nullable(),
  paymentMethodNote: z.string().nullable(),
  paymentProvider: z.string().nullable(),
  paymentReference: z.string().nullable(),
  cardBrand: z.string().nullable(),
  cardLast4: z.string().nullable(),
  depositPaidAmount: z.number().nullable(),
  balanceDue: z.number().nullable(),
  balanceCollectionMethod: z.string().nullable(),
  balanceDueBy: z.string().datetime().nullable(),
  refundedAt: z.string().datetime().nullable(),
  refundAmount: z.number().nullable(),
  disputedAt: z.string().datetime().nullable(),
});

export type BookingPaymentStory = z.infer<typeof bookingPaymentStorySchema>;

export function canMarkBookingPaid(input: {
  bookingStatus: string;
  paymentStatus: string;
  paymentRequirement: string;
}): boolean {
  return input.bookingStatus !== 'CANCELLED'
    && input.paymentRequirement !== 'NONE'
    && ['UNPAID', 'PENDING', 'FAILED'].includes(input.paymentStatus);
}

export interface RefundOwedInput {
  bookingStatus: string;
  paymentStatus: string;
  paymentProgressStatus: string | null;
  /** Dollars. */
  paymentAmount: number | null;
  /** Dollars. */
  depositPaidAmount: number | null;
  /** Dollars, CUMULATIVE already-refunded total. */
  refundAmount: number | null;
}

/** Money the client actually settled: the larger of a full payment and a deposit. */
export function settledBookingAmount(input: Pick<RefundOwedInput, 'paymentAmount' | 'depositPaidAmount'>): number {
  return Math.max(input.paymentAmount ?? 0, input.depositPaidAmount ?? 0);
}

/**
 * Dollars still owed back on a cancelled booking, 0 when nothing is owed.
 *
 * CANONICAL — the client open-loops read mirrors this predicate (`refundOwedLoops`
 * in `admin/clients/open-loops.ts`); the session-detail DTO flag and the
 * cancelled-booking refund band both call this. Changing it means changing
 * all three (see docs/data-model/dm-payments.md → admin refund-settlement writer).
 */
export function refundOwedAmount(input: RefundOwedInput): number {
  if (input.bookingStatus !== 'CANCELLED') return 0;
  const settledMoneyRecorded =
    ['PAID', 'REFUNDED'].includes(input.paymentStatus)
    || ['DEPOSIT_PAID', 'FULLY_PAID', 'REFUNDED'].includes(input.paymentProgressStatus ?? '');
  if (!settledMoneyRecorded) return 0;
  return Math.max(settledBookingAmount(input) - (input.refundAmount ?? 0), 0);
}
